import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Boxes, CheckCircle2, Sparkles, Lock, Clock, ArrowRight, HeartPulse, Check } from "lucide-react";
import { PageLoading } from "@/components/shared";
import api from "@/lib/api";

const STATUS = {
  active: { label: "Aktiv", c: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  inactive: { label: "Nicht aktiviert", c: "bg-slate-100 text-slate-600 border-slate-200" },
  soon: { label: "Bald verfügbar", c: "bg-cyan-50 text-cyan-dark border-cyan-200" },
};

function ModuleCard({ m, canBuy, onBuy, onUpgrade }) {
  const st = m.active ? STATUS.active : m.coming_soon ? STATUS.soon : STATUS.inactive;
  return (
    <div className="group relative flex flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-cyan/40 hover:shadow-[0_12px_40px_-16px_rgba(10,25,47,0.25)]" data-testid={`module-card-${m.key}`}>
      {m.recommended && !m.active && (
        <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-cyan-dark to-teal-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white" data-testid={`module-rec-${m.key}`}><Sparkles className="h-3 w-3" /> Für Ihre Branche empfohlen</span>
      )}
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-navy/5 text-navy"><Boxes className="h-5 w-5" /></div>
      <div className="font-heading text-lg font-extrabold text-navy">{m.name}</div>
      <p className="mt-1 flex-1 text-sm text-slate-500">{m.description}</p>
      <div className="mt-4 flex items-center gap-2">
        <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${st.c}`}>{st.label}</span>
        {m.sellable && !m.active ? <span className="text-[11px] text-slate-400">Professional / Unlimited</span> : null}
      </div>
      <div className="mt-4">
        {m.active ? (
          <div className="flex items-center gap-1.5 text-sm font-semibold text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Freigeschaltet</div>
        ) : m.coming_soon ? (
          <button disabled className="w-full cursor-not-allowed rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-400" data-testid={`module-soon-${m.key}`}>Bald verfügbar</button>
        ) : !canBuy ? (
          <button onClick={onUpgrade} className="flex w-full items-center justify-center gap-1.5 rounded-full border border-navy/20 bg-white px-4 py-2 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-white" data-testid={`module-upgrade-${m.key}`}><Lock className="h-3.5 w-3.5" /> Tarif wechseln</button>
        ) : (
          <button onClick={onBuy} className="flex w-full items-center justify-center gap-1.5 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-navy/90" data-testid={`module-buy-${m.key}`}>Modul kaufen <ArrowRight className="h-3.5 w-3.5" /></button>
        )}
      </div>
    </div>
  );
}

const fmt = (n) => Number(n).toLocaleString("de-DE");

function PflegeOffer({ nav }) {
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/employer/care-offer").then((r) => setO(r.data)).catch(() => {}); }, []);
  if (!o || !o.is_care_industry) return null;

  const st = o.state;
  const buyCare = async () => {
    setBusy("care");
    try { const r = await api.post("/employer/module/CARE_MODULE/checkout", { origin_url: window.location.origin }); window.location.href = r.data.checkout_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); setBusy(null); }
  };
  const buyBundle = async () => {
    setBusy("bundle");
    try { const r = await api.post("/employer/bundle/PFLEGE_COMPLETE_BUNDLE/checkout", { origin_url: window.location.origin }); window.location.href = r.data.checkout_url; }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (d === "TIME_ALREADY_ACTIVE") toast.error("Zeiterfassung ist bereits aktiv. Für einen Bundle-Wechsel kontaktieren Sie bitte den Support.");
      else if (d === "CARE_ALREADY_ACTIVE") toast.error("Pflege Modul ist bereits aktiv. Für einen Bundle-Wechsel kontaktieren Sie bitte den Support.");
      else toast.error(d || "Fehler");
      setBusy(null);
    }
  };

  const careActive = st.care_active;
  const bundleActive = st.care_active && st.time_active && (o.state.care_source === "PFLEGE_COMPLETE_BUNDLE");

  return (
    <div className="mb-10 rounded-3xl border border-cyan/30 bg-gradient-to-b from-cyan/5 to-white p-6 sm:p-8" data-testid="pflege-offer">
      <div className="flex items-center gap-2">
        <HeartPulse className="h-5 w-5 text-cyan-dark" />
        <h2 className="font-heading text-xl font-extrabold text-navy">Pflege für ambulante Pflegedienste</h2>
      </div>
      <p className="mt-1 max-w-2xl text-sm text-slate-500">Patienten, Touren, Besuche, Pflegeaufgaben und Dokumentation — verbunden mit Ihrer EmployGuard Plattform.</p>

      {!o.can_purchase && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="pflege-upgrade-banner">
          <Lock className="h-5 w-5 text-amber-600" />
          <div className="text-sm"><div className="font-semibold text-amber-800">Professional oder Unlimited erforderlich</div><div className="text-amber-700">Buchen Sie das Pflege Modul nach einem Tarifwechsel.</div></div>
          <button onClick={() => nav("/app/employer/billing")} className="ml-auto rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy/90" data-testid="pflege-change-plan">Tarif wechseln</button>
        </div>
      )}

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        {/* Pflege Modul */}
        <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6" data-testid="pflege-card-modul">
          <span className="w-fit rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">Modul Einzeln</span>
          <div className="mt-3 font-heading text-lg font-extrabold text-navy">Pflege Modul</div>
          <div className="mt-2 flex items-end gap-1"><span className="font-heading text-3xl font-extrabold text-navy">{fmt(o.care.price)} €</span><span className="mb-1 text-sm text-slate-500">/ Jahr</span></div>
          <div className="text-xs text-slate-400">zzgl. MwSt.</div>
          <div className="mt-4">
            {careActive ? (
              <div className="flex items-center gap-1.5 text-sm font-semibold text-emerald-600" data-testid="pflege-care-active"><CheckCircle2 className="h-4 w-4" /> Aktiv</div>
            ) : !o.can_purchase ? (
              <button disabled className="w-full cursor-not-allowed rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-400">Tarif erforderlich</button>
            ) : (
              <button onClick={buyCare} disabled={busy === "care"} className="flex w-full items-center justify-center gap-1.5 rounded-full border border-navy/20 bg-white px-4 py-2 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-white" data-testid="pflege-buy-modul">{busy === "care" ? "Weiterleitung…" : "Pflege Modul buchen"} <ArrowRight className="h-3.5 w-3.5" /></button>
            )}
          </div>
        </div>

        {/* Pflege Komplett */}
        <div className="relative flex flex-col rounded-2xl border-2 border-cyan bg-white p-6 shadow-[0_20px_60px_-30px_rgba(6,182,212,0.5)]" data-testid="pflege-card-komplett">
          <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-cyan-dark to-teal-600 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white"><Sparkles className="h-3 w-3" /> Empfohlen</span>
          <div className="mt-2 font-heading text-lg font-extrabold text-navy">Pflege Komplett</div>
          <div className="mt-2 flex items-end gap-2"><span className="font-heading text-3xl font-extrabold text-navy">{fmt(o.bundle.price)} €</span><span className="mb-1 text-sm text-slate-500">/ Jahr</span><span className="mb-1 text-sm text-slate-400 line-through">{fmt(o.bundle.individual_value)} €</span></div>
          <div className="text-xs text-slate-400">zzgl. MwSt. · inkl. Zeiterfassung</div>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700"><Sparkles className="h-3 w-3" /> {fmt(o.bundle.savings)} € jährlich sparen</span>
            <span className="rounded-full bg-cyan/10 px-2.5 py-1 text-[11px] font-bold text-cyan-dark">ca. {String(o.bundle.savings_pct).replace(".", ",")} % günstiger</span>
          </div>
          <div className="mt-4">
            {bundleActive ? (
              <div className="flex items-center gap-1.5 text-sm font-semibold text-emerald-600" data-testid="pflege-bundle-active"><CheckCircle2 className="h-4 w-4" /> Pflege Komplett aktiv</div>
            ) : st.time_independent ? (
              <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600" data-testid="pflege-time-active-note">Zeiterfassung ist bereits aktiv. Für einen Bundle-Wechsel kontaktieren Sie bitte den Support.</div>
            ) : st.care_independent ? (
              <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600" data-testid="pflege-care-active-note">Pflege Modul ist bereits aktiv. Für einen Bundle-Wechsel kontaktieren Sie bitte den Support.</div>
            ) : !o.can_purchase ? (
              <button disabled className="w-full cursor-not-allowed rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-400">Tarif erforderlich</button>
            ) : (
              <button onClick={buyBundle} disabled={busy === "bundle"} className="flex w-full items-center justify-center gap-1.5 rounded-full bg-cyan px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cyan-dark" data-testid="pflege-buy-komplett">{busy === "bundle" ? "Weiterleitung…" : "Pflege Komplett buchen"} <ArrowRight className="h-3.5 w-3.5" /></button>
            )}
          </div>
        </div>
      </div>

      {/* comparison */}
      <div className="mt-5 grid items-stretch gap-3 rounded-2xl bg-white/60 p-4 text-sm sm:grid-cols-2" data-testid="pflege-comparison">
        <div className="rounded-xl bg-slate-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Einzeln</div>
          <div className="mt-2 flex justify-between text-slate-600"><span>Pflege Modul</span><span className="font-semibold">{fmt(o.bundle.care_value)} €</span></div>
          <div className="flex justify-between text-slate-600"><span>Zeiterfassung</span><span className="font-semibold">{fmt(o.bundle.time_value)} €</span></div>
          <div className="mt-1.5 flex justify-between border-t border-slate-200 pt-1.5 font-heading font-extrabold text-navy"><span>Gesamt</span><span>{fmt(o.bundle.individual_value)} € / Jahr</span></div>
        </div>
        <div className="rounded-xl border-2 border-cyan/40 bg-cyan/5 p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-cyan-dark">Pflege Komplett</div>
          <div className="mt-2 font-heading text-2xl font-extrabold text-navy">{fmt(o.bundle.price)} € <span className="text-sm font-normal text-slate-500">/ Jahr</span></div>
          <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-bold text-white"><Check className="h-3.5 w-3.5" /> {fmt(o.bundle.savings)} € Ersparnis</div>
        </div>
      </div>
    </div>
  );
}

export default function Modules() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => { api.get("/bootstrap").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <PageLoading />;

  const canBuy = data.subscription?.can_purchase_premium_modules;
  const mods = data.modules || [];
  const active = mods.filter((m) => m.active);
  const recommended = mods.filter((m) => !m.active && m.recommended);
  const available = mods.filter((m) => !m.active && !m.recommended);
  const goBilling = () => nav("/app/employer/billing");

  const Section = ({ title, hint, items, testid }) => items.length === 0 ? null : (
    <div className="mb-10" data-testid={testid}>
      <div className="mb-1 font-heading text-xl font-extrabold text-navy">{title}</div>
      {hint && <p className="mb-4 text-sm text-slate-500">{hint}</p>}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((m) => <ModuleCard key={m.key} m={m} canBuy={canBuy} onBuy={goBilling} onUpgrade={goBilling} />)}
      </div>
    </div>
  );

  return (
    <div data-testid="modules-page">
      <div className="mb-2 flex items-center gap-2">
        <Boxes className="h-6 w-6 text-cyan-dark" />
        <h1 className="font-heading text-2xl font-extrabold text-navy">Module</h1>
      </div>
      <p className="mb-8 max-w-2xl text-sm text-slate-500">
        Erweitern Sie EmployGuard Core um branchenspezifische Module. Ihre Branche: <span className="font-semibold text-navy">{data.organization?.industry_label}</span>.
        {!canBuy && <span className="ml-1 text-amber-600">Premium-Module erfordern einen aktiven Professional- oder Unlimited-Tarif.</span>}
      </p>

      {!canBuy && (
        <div className="mb-8 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="modules-upgrade-banner">
          <Lock className="h-5 w-5 text-amber-600" />
          <div className="text-sm">
            <div className="font-semibold text-amber-800">Professional oder Unlimited erforderlich</div>
            <div className="text-amber-700">Premium-Module können mit einem aktiven Professional- oder Unlimited-Tarif gebucht werden.</div>
          </div>
          <button onClick={goBilling} className="ml-auto rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy/90" data-testid="modules-change-plan">Tarif wechseln</button>
        </div>
      )}

      <Section title="Aktive Module" items={active} testid="modules-active" />
      <PflegeOffer nav={nav} />
      <Section title="Für Ihre Branche empfohlen" hint="Passend zu Ihrer ausgewählten Branche." items={recommended} testid="modules-recommended" />
      <Section title="Verfügbare Module" items={available} testid="modules-available" />
    </div>
  );
}
